import {
  Button,
  Stack,
  Typography,
  useTheme
} from '@mui/material';
import { appFormUtil } from 'modules/common';
import { FormBaseLayout } from 'modules/components/layer10';
import { StyledTextField } from 'modules/components/layer01';
import { CustomIcon } from 'modules/components/layer00';
import {
  DeliveryInstallationSelectField,
  UploadAttachmentsField
} from 'modules/components/layer04';
import { AttachmentCard } from 'modules/components/layer05';
import React from 'react';

import * as ds from './dataService';
import _ from 'lodash';

import { SaveDelivery_Input } from 'generated/graphql';
import { DateTime } from 'luxon';



export type SaveDeliveryFormProps = {
  installationId?: string;
  projectId?: string;
  handleClose: () => void;
  id: string;
};


/* eslint-disable-next-line @typescript-eslint/no-unused-vars */
export const FC = (props: SaveDeliveryFormProps) => {
  const theme = useTheme();
  const form = ds.useFormState(props.id, props.installationId ?? '');

  const [
    fnSaveDelivery,
    { loading }
  ] = ds.useSaveDelivery();

  const [fnRemoveImage] = ds.useRemoveImageFromDelivery();

  const handleSubmit = async (input: ds.IFormState) => {

    const tempInput: SaveDelivery_Input = {
      id: input.id,
      installationId: input.installationId,
      damageDescription: input.damages,
      issueDescription: input.issues,
      description: input.description,
      name: input.materialName,
      otherDescription: input.otherEvents,
      quantity: _.toNumber(input.quantity),
      receivedDate: input.receivedDate === '' ? null : DateTime.fromFormat(input.receivedDate, 'yyyy-LL-dd'),
      fileIds: input.attachments.map((node) => node.id ?? '')
    };

    if ((tempInput.fileIds && tempInput.fileIds?.length < 1) || !tempInput.fileIds) {
      delete tempInput.fileIds;
    }

    await fnSaveDelivery(tempInput);


    props.handleClose();
  };


  const rightFields: (keyof ds.IFormState)[] = [
    'damages',
    'issues',
    'otherEvents'
  ];

  const removeAttachment = (id: string, deliveryId: string) => {
    fnRemoveImage({
      fileId: id,
      deliveryId: deliveryId
    });
    form.dispatch({
      type: 'REMOVE_FROM_ARRAY',
      payload: {
        key: 'attachments',
        value: id,
        identifier: 'id'
      }
    });
  };

  return (
    <FormBaseLayout.FC
      handleCancel={() => props.handleClose()}
      handleSubmit={() => {
        handleSubmit(form.formState);
      }}
      cancelButtonText='Cancel'
      successButtonText='Save'
      isLoading={loading}
    >
      <Stack
        direction='row'
        columnGap='2rem'
      >
        {/* LEFT SIDE OF FORM */}
        <Stack
          flex='1 1 0'
          direction='column'
          rowGap='1.5rem'
          width='40rem'
        >

          <StyledTextField.FC
            {...form.genFieldProps('receivedDate')}
          />

          <StyledTextField.FC
            {...form.genFieldProps('materialName')}
          />
          <StyledTextField.FC
            {...form.genFieldProps('quantity')}
          />
          <StyledTextField.FC
            {...form.genFieldProps('description')}
            minRows={4}
          />
          {
            __isConnectedToInstallation(form)
              ? (
                <Stack
                  padding='1.5rem'
                  bgcolor={theme.palette.secondary.main}
                  borderRadius='1rem'

                  rowGap='1rem'
                >
                  <Typography
                    variant='modalTitle'
                  >
                    Does this delivery contain finishing product?
                  </Typography>
                  <Typography
                    variant='body1'
                  >
                    Connect this delivery to specific finish details to change their status to Delivered. You can do this later by editing daily report or by connecting delivery in finish details.
                  </Typography>
                  <Button
                    variant='contained'
                    color='primary'
                    onClick={() => form.dispatch({
                      type: 'UPDATE',
                      payload: {
                        property: 'connectedToInstallation',
                        value: true
                      }
                    })}
                    startIcon={(
                      <CustomIcon.large.FC
                        icon='SPARKLE'
                        stroke={theme.palette.primary.contrastText}
                      />
                    )}
                  >
                    Connect to Finish
                  </Button>
                </Stack>
              )
              : (

                <DeliveryInstallationSelectField.FC
                  form={form}
                  fieldName='installationId'
                  projectId={props.projectId ?? ''}
                />
              )
          }

        </Stack>
        {/* RIGHT SIDE OF FORM */}
        <Stack
          flex='1 1 0'
          direction='column'
          rowGap='1.5rem'
        >
          {rightFields.map((node, i) => (
            <StyledTextField.FC
              key={i}
              {...form.genFieldProps(node)}
            />
          ))}
          <UploadAttachmentsField.FC
            form={form}
            fieldName='attachments'
          />

          {form.formState.attachments.map((node) => (
            <AttachmentCard.FC
              key={node.id}
              url={node.url ?? ''}
              fileName={node.fileName ?? ''}
              formattedFileSize={node.formattedFileSize ?? ''}
              handleDelete={() => {
                removeAttachment(node.id ?? '', form.formState.id);
              }}
              id={node.id ?? ''}
            />
          ))}
        </Stack>
      </Stack>
    </FormBaseLayout.FC>
  );
};
function __isConnectedToInstallation(form: appFormUtil.InferReturn<ds.IFormState>) {
  return (
    !form.formState.connectedToInstallation
  && form.formState.installationId === ''
  && _.isNull(form.formState.installationId)
  );
}

